/**
 * EntityPreviewCard — shows what a CRM entity would look like from sample data.
 *
 * Supports two modes:
 * 1. **Sample mode** (default): Displays pre-extracted sample rows with prev/next.
 * 2. **Search mode**: When a query is entered, streams the full CSV file via
 *    PapaParse to find matching rows across all columns.
 *
 * Extracted from MappingStep.tsx for modularity.
 */

import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Loading02,
  SearchLg,
  XClose,
} from '@untitledui/icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { IconButton } from '@/lib/base/buttons/button-utility';
import { Input } from '@/lib/base/forms/input/input';
import { PipelineStageValueLabel } from '@/lib/crm/components/properties/labels/PipelineStageValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema as CrmPropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type {
  CrmEntityAssociationMapping,
  FixedValueMapping,
  NewPropertySuggestion,
} from '@/lib/csv-import/api';

import { useFileSearch } from '../../hooks/useFileSearch';
import { useOrgUsers } from '../../hooks/useOrgUsers';
import { getEntityTypeLabel } from '../../utils/entityTypeLabels';
import { normalizePreviewValue } from '../../utils/previewValueNormalization';
import {
  buildNewPropertyIndex,
  EMPTY_ARRAY,
  EMPTY_NEW_PROPERTIES,
  fetchPropertyTypes,
  getStageLabelForSource,
  humanisePropertyName,
  propertyIndexKey,
} from './mapping-constants';
import {
  findStageAssociationMapping,
  resolvePreviewOwnerVisibility,
  resolvePreviewStageEntityId,
} from './previewFallbackValues';

/** Only show the search filter when there are at least this many mapped fields. */
const SEARCH_THRESHOLD = 8;

/** Stable empty defaults for the optional mapping-config props — an inline
 * `= []` default creates a fresh reference every render, defeating memo(). */
const EMPTY_ASSOC_MAPPINGS: CrmEntityAssociationMapping[] = [];
const EMPTY_FIXED_VALUE_MAPPINGS: FixedValueMapping[] = [];

/**
 * Resolved stage value + provenance microcopy, shared between the per-row
 * field list (stage column mapped — the value varies by row) and the
 * "Applied to every record" section (fallback only — the value is global).
 * The preview trusts the resolved stage UUID offline (no CRM liveness
 * check): a stale/deleted mapped stage still renders via
 * PipelineStageValueLabel's graceful fallback here, while execute
 * reclassifies it as unmatched and applies the fallback stage. This narrow
 * preview/execute divergence window is accepted.
 */
function StageValueCell({
  stageEntityId,
  contextLine,
}: {
  stageEntityId: string | null;
  contextLine: string;
}) {
  return (
    <div className='text-sm text-fg-primary flex-1 min-w-0'>
      <div className='truncate'>
        {stageEntityId ? (
          <PipelineStageValueLabel value={stageEntityId} fontSize='sm' />
        ) : (
          <span className='text-fg-quaternary italic'>No stage</span>
        )}
      </div>
      <div className='truncate text-xs text-fg-quaternary'>{contextLine}</div>
    </div>
  );
}

export interface EntityPreviewCardProps {
  /** Organization ID for fetching property type schemas. */
  orgId: number;
  /** Entity type for display label (e.g. "ahoy_contact" -> "Contact"). */
  entityTypeName: string;
  /** Sample CSV rows (up to 5). */
  sampleRows: Record<string, string>[];
  /** Current column mappings: csvColumn -> propertyName. */
  mappings: Record<string, string>;
  /** Total row count for the file. */
  rowCount?: number;
  /** Original file (or CSV-converted blob for XLSX) for streaming search. */
  csvFile?: File | Blob;
  /** Column headers from the file. */
  headers?: string[];
  /** User-created new properties for this file (all entity types; the resolver filters by entity_type_name). */
  userNewProperties?: NewPropertySuggestion[];
  /** AI-suggested new properties for this entity type. */
  aiNewProperties?: NewPropertySuggestion[];
  /** Virtual-key id used as `source_file_id` in stage/fixed-value mappings. */
  sourceFileId?: string;
  /** Stage-typed CRM entity association mappings, for stage column resolution. */
  crmEntityAssociationMappings?: CrmEntityAssociationMapping[];
  /** Fixed owner/visibility mappings applied to every record of this file/type. */
  fixedValueMappings?: FixedValueMapping[];
  /** Fallback stage entity id applied when no per-row mapping matches. */
  defaultStage?: string;
}

export const EntityPreviewCard = memo(function EntityPreviewCard({
  orgId,
  entityTypeName,
  sampleRows,
  mappings,
  rowCount,
  csvFile,
  headers = EMPTY_ARRAY,
  userNewProperties = EMPTY_NEW_PROPERTIES,
  aiNewProperties = EMPTY_NEW_PROPERTIES,
  sourceFileId,
  crmEntityAssociationMappings = EMPTY_ASSOC_MAPPINGS,
  fixedValueMappings = EMPTY_FIXED_VALUE_MAPPINGS,
  defaultStage,
}: EntityPreviewCardProps) {
  const entityLabel = getEntityTypeLabel(entityTypeName);
  const stageLabel = getStageLabelForSource(entityTypeName);
  const [rowIndex, setRowIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleClearFieldFilter = useCallback(() => {
    setFieldFilter('');
  }, []);

  // Stream-search the full file when a query is entered.
  const {
    results: searchResults,
    isSearching,
    activeQuery,
  } = useFileSearch(csvFile, headers, searchQuery);

  // Decide which rows to display: search results or sample rows.
  const isInSearchMode = activeQuery.length > 0;
  const displayRows = isInSearchMode ? searchResults : sampleRows;

  // The CSV columns that are actually mapped to a property.
  const mappedColumns = useMemo(
    () =>
      Object.entries(mappings)
        .filter(([, prop]) => prop && prop !== '__skip__')
        .map(([col]) => col),
    [mappings]
  );

  // Pick the most-populated sample row so the preview shows a meaningful example
  // rather than whatever happens to be first (often sparse for these records).
  const bestRowIndex = useMemo(() => {
    if (isInSearchMode) {
      return 0;
    }
    let best = 0;
    let bestCount = -1;
    sampleRows.forEach((row, i) => {
      const count = mappedColumns.reduce(
        (n, col) => n + ((row[col] ?? '').trim() ? 1 : 0),
        0
      );
      if (count > bestCount) {
        bestCount = count;
        best = i;
      }
    });
    return best;
  }, [sampleRows, mappedColumns, isInSearchMode]);

  // Reset to the most-populated row when the sample set (or mapping) changes.
  useEffect(() => {
    setRowIndex(bestRowIndex);
  }, [bestRowIndex]);

  // Reset row index when search results change.
  useEffect(() => {
    if (isInSearchMode) {
      setRowIndex(0);
    }
  }, [searchResults, isInSearchMode]);

  const handlePrevRow = useCallback(() => {
    setRowIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextRow = useCallback(() => {
    setRowIndex(prev => Math.min(displayRows.length - 1, prev + 1));
  }, [displayRows.length]);

  // Fetch property types to get labels and field type info for value rendering.
  // Same query key as FileMappingSection -> React Query deduplicates and caches.
  const { data: propertyTypes = [] } = useQuery({
    queryKey: ['propertyTypes', orgId, entityTypeName],
    queryFn: () => fetchPropertyTypes(orgId, entityTypeName),
    staleTime: 5 * 60 * 1000,
  });

  // Build a lookup map: property_type_name -> schema
  const propertySchemaMap = useMemo(() => {
    const map = new Map<string, CrmPropertyTypeSchema>();
    for (const pt of propertyTypes) {
      // Cast to CrmPropertyTypeSchema — the local PropertyTypeSchema is a
      // compatible subset, and the endpoint returns the full shape.
      map.set(pt.property_type_name, pt as unknown as CrmPropertyTypeSchema);
    }
    return map;
  }, [propertyTypes]);

  const currentRow = displayRows[rowIndex];

  const mappedFields = useMemo(() => {
    if (!currentRow) {
      return [];
    }
    // Build the index once — O(1) per-field lookup instead of O(n) per field.
    const newPropertyIndex = buildNewPropertyIndex(
      userNewProperties,
      aiNewProperties
    );
    const fields: Array<{
      propertyName: string;
      csvColumn: string;
      rawValue: string;
      displayValue: unknown;
      schema: CrmPropertyTypeSchema | undefined;
      label: string;
    }> = [];

    for (const [csvColumn, propertyName] of Object.entries(mappings)) {
      if (!propertyName || propertyName === '__skip__') {
        continue;
      }
      const rawValue = currentRow[csvColumn] ?? '';
      const schema = propertySchemaMap.get(propertyName);
      const displayValue = normalizePreviewValue(
        rawValue,
        schema?.field_type_name
      );
      const np = newPropertyIndex.get(
        propertyIndexKey(propertyName, entityTypeName)
      );
      const label =
        (np && np.label.trim() ? np.label : undefined) ??
        schema?.label ??
        humanisePropertyName(propertyName);
      fields.push({
        propertyName,
        csvColumn,
        rawValue,
        displayValue,
        schema,
        label,
      });
    }
    return fields;
  }, [
    currentRow,
    mappings,
    propertySchemaMap,
    userNewProperties,
    aiNewProperties,
    entityTypeName,
  ]);

  const showFieldFilter = mappedFields.length >= SEARCH_THRESHOLD;

  const filteredFields = useMemo(() => {
    if (!fieldFilter.trim()) {
      return mappedFields;
    }
    const q = fieldFilter.toLowerCase().trim();
    return mappedFields.filter(({ label, rawValue }) => {
      return (
        label.toLowerCase().includes(q) || rawValue.toLowerCase().includes(q)
      );
    });
  }, [mappedFields, fieldFilter]);

  // Stage/owner/visibility applied to every record of this file/type, mirroring
  // the same fallback resolution the backend uses at execute time.
  const { users } = useOrgUsers(orgId);

  const userDisplayNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users) {
      map.set(u.userId, u.displayName);
    }
    return map;
  }, [users]);

  const stageMapping = useMemo(
    () =>
      findStageAssociationMapping(
        crmEntityAssociationMappings,
        sourceFileId ?? '',
        entityTypeName
      ),
    [crmEntityAssociationMappings, sourceFileId, entityTypeName]
  );

  const stageEntityId = useMemo(
    () => resolvePreviewStageEntityId(stageMapping, currentRow, defaultStage),
    [stageMapping, currentRow, defaultStage]
  );

  const { ownerUserId, visibility } = useMemo(
    () =>
      resolvePreviewOwnerVisibility(
        fixedValueMappings,
        sourceFileId ?? '',
        entityTypeName
      ),
    [fixedValueMappings, sourceFileId, entityTypeName]
  );

  // With a stage column mapped the stage is row-derived, so it renders in the
  // per-row field list; only a column-less fallback is truly "applied to every
  // record" and belongs in that section.
  const stageColumnMapped = Boolean(stageMapping?.source_column);
  const stageFallbackOnly = !stageColumnMapped && Boolean(defaultStage?.trim());
  // Only treat the owner as "applied" once it resolves to a real org user. If
  // the configured owner isn't in useOrgUsers (still loading, or a target that
  // no longer resolves), execute drops it and creates the record unassigned —
  // so gate the row on the resolved name, not the raw id, to avoid showing a
  // blank owner row that misrepresents an unassigned record as assigned.
  const ownerName =
    ownerUserId != null ? userDisplayNameById.get(ownerUserId) : undefined;
  const ownerResolved = ownerName != null;

  if (!currentRow && !isSearching && mappedFields.length === 0) {
    // No data at all — hide the card if we also have no sample rows.
    if (sampleRows.length === 0) {
      return null;
    }
  }

  const headerLabel = isInSearchMode
    ? `${entityLabel} Preview – ${searchResults.length === 1 ? '1 match' : `${searchResults.length} matches`}`
    : `${entityLabel} Preview`;

  return (
    <div className='border border-border-secondary rounded-lg bg-bg-primary overflow-hidden'>
      <div className='px-4 py-2.5 border-b border-border-secondary bg-bg-secondary flex items-center justify-between'>
        <h4 className='text-xs font-semibold uppercase tracking-wide text-fg-quaternary'>
          {headerLabel}
          {!isInSearchMode && rowCount !== undefined && (
            <span className='font-normal normal-case tracking-normal ml-1'>
              – Row {rowIndex + 1} of {rowCount.toLocaleString()}
            </span>
          )}
          {isInSearchMode && searchResults.length > 1 && (
            <span className='font-normal normal-case tracking-normal ml-1'>
              – {rowIndex + 1} of {searchResults.length}
            </span>
          )}
        </h4>
        <div className='flex items-center gap-1'>
          {isSearching && (
            <Loading02 className='size-4 text-fg-quaternary animate-spin' />
          )}
          {displayRows.length > 1 && (
            <div className='flex items-center gap-0.5'>
              <IconButton
                icon={ChevronLeft}
                size='xs'
                color='tertiary'
                tooltip='Previous row'
                onClick={handlePrevRow}
                isDisabled={rowIndex === 0}
              />
              <IconButton
                icon={ChevronRight}
                size='xs'
                color='tertiary'
                tooltip='Next row'
                onClick={handleNextRow}
                isDisabled={rowIndex === displayRows.length - 1}
              />
            </div>
          )}
        </div>
      </div>

      {/* Row search — searches across all CSV rows via streaming */}
      {csvFile && (
        <div className='px-3 py-2 border-b border-border-secondary'>
          <Input
            type='text'
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder='Search all rows'
            aria-label='Search CSV rows'
            icon={SearchLg}
            size='xs'
            trailingAddon={
              searchQuery ? (
                <IconButton
                  size='xs'
                  color='tertiary'
                  icon={XClose}
                  onClick={handleClearSearch}
                  aria-label='Clear search'
                  className='p-0.5'
                />
              ) : undefined
            }
          />
        </div>
      )}

      {/* Field filter — filters visible fields within the current row */}
      {showFieldFilter && (
        <div className='px-3 py-2 border-b border-border-secondary'>
          <Input
            type='text'
            value={fieldFilter}
            onChange={setFieldFilter}
            placeholder='Filter fields'
            aria-label='Filter preview fields'
            icon={SearchLg}
            size='xs'
            trailingAddon={
              fieldFilter ? (
                <IconButton
                  size='xs'
                  color='tertiary'
                  icon={XClose}
                  onClick={handleClearFieldFilter}
                  aria-label='Clear filter'
                  className='p-0.5'
                />
              ) : undefined
            }
          />
        </div>
      )}

      {/* Search state feedback */}
      {isSearching && (
        <div className='px-4 py-3 text-xs text-fg-quaternary text-center'>
          Searching…
        </div>
      )}

      {!isSearching &&
        isInSearchMode &&
        searchResults.length === 0 &&
        searchQuery.trim() && (
          <div className='px-4 py-3 text-xs text-fg-quaternary text-center'>
            No rows match &ldquo;{activeQuery}&rdquo;
          </div>
        )}

      {/* Field list */}
      {currentRow && !isSearching && (
        <div className='divide-y divide-border-tertiary'>
          {filteredFields.length === 0 && fieldFilter ? (
            <div className='px-4 py-3 text-xs text-fg-quaternary text-center'>
              No fields match &ldquo;{fieldFilter}&rdquo;
            </div>
          ) : (
            filteredFields.map(
              ({ csvColumn, rawValue, displayValue, schema, label }) => (
                <div
                  key={csvColumn}
                  className='flex items-baseline gap-3 px-4 py-2'
                >
                  <span
                    className='text-xs font-medium text-fg-tertiary w-32 shrink-0 truncate'
                    title={label}
                  >
                    {label}
                  </span>
                  <span className='text-sm text-fg-primary truncate flex-1'>
                    {rawValue ? (
                      <PropertyValueLabel
                        value={displayValue}
                        propertyType={schema}
                        fontSize='sm'
                        truncate
                      />
                    ) : (
                      <span className='text-fg-quaternary italic'>empty</span>
                    )}
                  </span>
                </div>
              )
            )
          )}
          {/* Row-derived stage: with a stage column mapped, the stage this
              specific row resolves to (its mapped column value, or the
              configured fallback when unmatched) belongs with the other
              per-row values, not in the globals section below. Not subject
              to the field filter — it isn't a mapped column field. */}
          {stageColumnMapped && (
            <div className='flex items-baseline gap-3 px-4 py-2'>
              <span
                className='text-xs font-medium text-fg-tertiary w-32 shrink-0 truncate'
                title={stageLabel}
              >
                {stageLabel}
              </span>
              <StageValueCell
                stageEntityId={stageEntityId}
                contextLine={`From ${stageMapping?.source_column} (fallback when unmatched)`}
              />
            </div>
          )}
        </div>
      )}

      {/* Values genuinely applied to every record of this file/type: the
          column-less fallback stage and the owner/visibility fallbacks. A
          row-derived stage (column mapped) renders in the per-row field
          list above instead. */}
      {(stageFallbackOnly || ownerResolved || Boolean(visibility)) && (
        <section
          aria-label='Applied to every record'
          className='border-t border-border-secondary'
        >
          <h4 className='px-4 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-fg-quaternary'>
            Applied to every record
          </h4>
          <ul className='divide-y divide-border-tertiary'>
            {stageFallbackOnly && (
              <li className='flex items-baseline gap-3 px-4 py-2'>
                <span className='text-xs font-medium text-fg-tertiary w-32 shrink-0 truncate'>
                  {stageLabel}
                </span>
                <StageValueCell
                  stageEntityId={stageEntityId}
                  contextLine='Every imported record'
                />
              </li>
            )}
            {ownerResolved && (
              <li className='flex items-baseline gap-3 px-4 py-2'>
                <span className='text-xs font-medium text-fg-tertiary w-32 shrink-0 truncate'>
                  Owner
                </span>
                <span className='text-sm text-fg-primary truncate flex-1'>
                  {ownerName}
                </span>
              </li>
            )}
            {visibility && (
              <li className='flex items-baseline gap-3 px-4 py-2'>
                <span className='text-xs font-medium text-fg-tertiary w-32 shrink-0 truncate'>
                  Visibility
                </span>
                <span className='text-sm text-fg-primary truncate flex-1'>
                  {visibility === 'private' ? 'Private' : 'Public'}
                </span>
              </li>
            )}
          </ul>
        </section>
      )}
    </div>
  );
});
